import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getUserRoles, createUserRole, updateUserRole, deleteUserRole } from './PermissionCRUD';
import { UserRoleState } from '../models/Role';

// Async thunks
export const fetchUserRoles = createAsyncThunk(
  'userRoles/fetchUserRoles',
  async () => {
    const response = await getUserRoles();
    return response;
  }
);

export const addUserRole = createAsyncThunk(
  'userRoles/addUserRole',
  async (userRoleData: { user: string; role: string; company: string; is_primary?: boolean }) => {
    const response = await createUserRole(userRoleData);
    return response;
  }
);

export const editUserRole = createAsyncThunk(
  'userRoles/editUserRole',
  async ({ id, userRoleData }: { id: string; userRoleData: any }) => {
    const response = await updateUserRole(id, userRoleData);
    return response;
  }
);

export const removeUserRole = createAsyncThunk(
  'userRoles/removeUserRole',
  async (id: string) => {
    await deleteUserRole(id);
    return id;
  }
);



const initialState: UserRoleState = {
  userRoles: [],
  loading: false,
  error: null,
};

// Slice
const userRoleSlice = createSlice({
  name: 'userRoles',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUserRoleSelected: (state, action) => {
      const { id, selected } = action.payload;
      const userRole = state.userRoles.find(ur => ur.id === id);
      if (userRole) {
        (userRole as any).selected = selected;
      }
    },
    clearUserRoleSelection: (state) => {
      state.userRoles.forEach(userRole => {
        (userRole as any).selected = false;
      });
    },
  },
  extraReducers: (builder) => {
    // Fetch user roles
    builder
      .addCase(fetchUserRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.userRoles = action.payload;
      })
      .addCase(fetchUserRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch user roles';
      });

    // Add user role
    builder
      .addCase(addUserRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addUserRole.fulfilled, (state, action) => {
        state.loading = false;
        state.userRoles.push(action.payload);
      })
      .addCase(addUserRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to add user role';
      });

    // Edit user role
    builder
      .addCase(editUserRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editUserRole.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.userRoles.findIndex(ur => ur.id === action.payload.id);
        if (index !== -1) {
          state.userRoles[index] = action.payload;
        }
      })
      .addCase(editUserRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to edit user role';
      });

    // Remove user role
    builder
      .addCase(removeUserRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeUserRole.fulfilled, (state, action) => {
        state.loading = false;
        state.userRoles = state.userRoles.filter(ur => ur.id !== action.payload);
      })
      .addCase(removeUserRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to remove user role';
      });
  },
});

// Actions
export const { clearError, setUserRoleSelected, clearUserRoleSelection } = userRoleSlice.actions;

// Selectors
export const selectUserRoles = (state: any) => state.userRoles.userRoles;
export const selectUserRolesLoading = (state: any) => state.userRoles.loading;
export const selectUserRolesError = (state: any) => state.userRoles.error;
export const selectSelectedUserRoles = (state: any) => state.userRoles.userRoles.filter((ur: any) => ur.selected);

// Export actions object for compatibility
export const actions = {
  actions: userRoleSlice.actions,
  fetchUserRoles,
  addUserRole,
  editUserRole,
  removeUserRole,
};

export default userRoleSlice.reducer;
